import { t } from '@lingui/core/macro';

type OptionLike = { id: string };

// Only the parts of the flow we actually read: each node's option list and
// label, and each edge's source handle (an option id when the edge leaves an
// option). Narrower than ReactFlowJsonObject so callers and tests can pass a
// minimal shape without casts.
type FlowToValidate = {
  nodes: { data?: { label?: string; options?: OptionLike[] } }[];
  edges: { sourceHandle?: string | null }[];
};

// How many block names to spell out before falling back to a "+N more"
// summary, so the publish toast stays readable when many blocks are incomplete.
const MAX_BLOCKS = 3;

// A block's quoted title, or a quoted placeholder when it has no title, so it
// slots into "Block ${name}" and the list the same way either way.
const blockName = (label: string | undefined): string => {
  const trimmed = label?.trim();
  return trimmed ? `"${trimmed}"` : t`"Untitled"`;
};

export const validateFlowEditorState = (
  state: FlowToValidate,
): {
  isValid: boolean;
  message: string | null;
} => {
  const connectedHandles = new Set(state.edges.map((e) => e.sourceHandle));

  // The blocks that still have at least one option with no outgoing edge. We
  // point the author at the block, not the individual option: the amber
  // connection handle on the canvas already pinpoints which option to connect.
  const names = state.nodes
    .filter((node) => (node.data?.options ?? []).some((option) => !connectedHandles.has(option.id)))
    .map((node) => blockName(node.data?.label));

  if (names.length === 0) {
    return { isValid: true, message: null };
  }

  if (names.length === 1) {
    const name = names[0] ?? '';
    return {
      isValid: false,
      message: t`Block ${name} has an unconnected option.`,
    };
  }

  const shown = names.slice(0, MAX_BLOCKS);
  const extra = names.length - shown.length;
  // Plain comma-joined list, plus a "+N more" tail when capped. Simple over
  // perfectly grammatical; no Oxford comma or "and" joining needed.
  const list = extra > 0 ? `${shown.join(', ')} and ${extra} more` : shown.join(', ');

  return {
    isValid: false,
    message: t`These blocks have unconnected options: ${list}.`,
  };
};
